import React, { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { formatCurrency } from "../../utility";
import { useSelector } from "react-redux";

const QrCodeLabel = ({
  itemPrice,
  itemName = "",
  value,
  backgroundColor,
  labelWidth,
  labelHeight,
  batteryHealth,
  category,
  showPrice = true,
  showBatteryHealth = true,
  deviceMode = 'both',
}) => {
  const { companies } = useSelector((state) => state.companyReducer);

  const companyName = useMemo(() => {
    const company = companies?.find((comp) => comp.company_name);
    return company ? company.company_name : "Ishop Master";
  }, [companies]);

  const isDevice = useMemo(() => {
    return category?.toLowerCase() === "device";
  }, [category]);

  const itemNameFontSize = useMemo(() => {
    const len = itemName?.length || 0;
    if (len > 40) return "1.4mm";
    if (len > 25) return "1.6mm";
    if (len > 15) return "1.8mm";
    return "2.0mm";
  }, [itemName]);

  const parsedPrice = useMemo(() => {
    const num = Number(itemPrice);
    return isNaN(num) ? 0 : num;
  }, [itemPrice]);

  return (
    <div
      className="label"
      style={{
        width: `${labelWidth}mm`,
        height: `${labelHeight}mm`,
        display: "flex",
        flexDirection: "row", // Side-by-side layout
        padding: "0.8mm 1.2mm",
        backgroundColor: backgroundColor || "#ffffff",
        boxSizing: "border-box",
        border: "none",
        margin: 0,
        gap: "1.2mm",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        overflow: "hidden",
      }}
    >
      {/* Left Side: 32% QR Code */}
      <div
        style={{
          width: "32%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <QRCodeSVG 
          value={value || " "} 
          size={100}
          style={{ 
            width: "100%", 
            height: "auto",
            maxHeight: "13mm", 
            maxWidth: "13mm" 
          }} 
          level="L" 
        />
        {/* Print value underneath QR Code */}
        <div style={{
          fontSize: "1.2mm",
          fontWeight: "900",
          textAlign: "center",
          width: "100%",
          fontFamily: "monospace",
          marginTop: "0.4mm",
          wordBreak: "break-all",
          lineHeight: 1,
          letterSpacing: "-0.2px",
        }}>
           {value}
        </div>
      </div>

      {/* Right Side: 68% Text Details */}
      <div
        style={{
          width: "68%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        {/* Item Name Area (Wraps into 2-3 lines) */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            overflow: "hidden",
            fontWeight: "900",
            fontSize: itemNameFontSize,
            lineHeight: 1.05,
            textTransform: 'uppercase',
          }}
        >
          <div style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}>
            {itemName}
          </div>
          {showBatteryHealth && isDevice && (
            <div style={{ fontSize: "1.3mm", fontWeight: "bold", marginTop: "0.2mm" }}>
              B/H: {batteryHealth || 0}%
            </div>
          )}
        </div>

        {/* Price and Company Area */}
        <div
          style={{
            display: "flex",
            justifyContent: showPrice ? "space-between" : "flex-end",
            alignItems: "flex-end",
            width: "100%",
            lineHeight: 1,
            marginTop: "0.4mm",
          }}
        >
          {showPrice && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
              <span style={{ fontSize: "1.0mm", opacity: 0.85, fontWeight: "700" }}>RS.</span>
              <span style={{ fontSize: "1.9mm", fontWeight: "900", lineHeight: 1 }}>
                {parsedPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          <div style={{ 
            textAlign: "right", 
            whiteSpace: "nowrap",
            textTransform: 'uppercase',
            lineHeight: 1,
          }}>
            <span style={{ fontSize: "1.4mm", fontWeight: "900", letterSpacing: "0.1px" }}>{companyName}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrCodeLabel;
